import React, { useState } from "react";
import { UserCog, LogOut, Loader2 } from "lucide-react";
import { auth as authLib } from "../lib/api";

/**
 * ImpersonationBanner — fixed top strip that shows whenever an admin has
 * swapped into another user's session via /admin/users → Log-in-as.
 *
 * One-click "Return to admin" restores the stashed admin token + user
 * object (saved before the swap) and reloads to `/admin/users` so the
 * admin lands right where they were.
 */
export default function ImpersonationBanner() {
  const stashed = authLib.getStashedAdmin();
  const user = authLib.getUser();
  const [busy, setBusy] = useState(false);
  if (!stashed || !user) return null;

  const restore = () => {
    setBusy(true);
    authLib.setToken(stashed.token);
    authLib.setUser(stashed.user);
    authLib.clearStashedAdmin();
    window.location.href = "/admin/users";
  };

  return (
    <div
      className="relative z-[60] bg-amber-500 text-black text-xs font-mono-data tracking-wide shadow-md"
      data-testid="impersonation-banner"
      role="alert"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <UserCog className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">
            <span className="font-bold uppercase">Impersonating</span>
            <span className="mx-1">·</span>
            <span className="font-bold">{user.email}</span>
            <span className="opacity-70 ml-1 hidden sm:inline">(as {stashed.user?.email})</span>
          </span>
        </div>
        <button
          type="button"
          onClick={restore}
          disabled={busy}
          className="flex items-center gap-1.5 bg-black text-amber-400 hover:bg-neutral-900 px-3 py-1 rounded-md font-bold uppercase tracking-widest text-[10px] transition disabled:opacity-50"
          data-testid="impersonation-return-btn"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />}
          Return to admin
        </button>
      </div>
    </div>
  );
}
